import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import { formatDateTime } from '../utils/datetime';
import { apiErrorMessage } from '../utils/apiError';

// Access profiles — the list.
//
// A profile is a NAMED, reusable grant: the routes an integration of some kind gets, and the redaction
// each call is served with. It exists so the same answer can be given to fifty properties without being
// retyped fifty times and drifting on the ones nobody revisited.
//
// NOT per property. uapi_hotel_access binds one hotel to one backend with one credential, and its module
// grants answer "what may THIS hotel call". A profile answers that once for a class of integration.
//
// WHO FOLLOWS IT is part of the profile (V465): a chain, hotels, or single inbound credentials. The
// counts in the Governs column are per scope rather than totalled, because a profile on a whole chain
// and one on a single credential are very different amounts of estate to be changing when somebody
// edits its routes.
// Who a profile governs, in words. A count per scope, and the scopes with none left out entirely —
// "0 chains, 2 hotels, 0 credentials" makes the reader do the filtering the screen should have done.
const governs = (p) => [
    [p.chain_count, 'chain', 'chains'],
    [p.hotel_count, 'hotel', 'hotels'],
    [p.credential_count, 'credential', 'credentials'],
].filter(([n]) => Number(n) > 0)
    .map(([n, one, many]) => `${n} ${Number(n) === 1 ? one : many}`)
    .join(', ');

const UapiAccessProfilesTab = () => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await legacyUapiService.listAccessProfiles();
            setProfiles((res.data || res).profiles || []);
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to load access profiles'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = (p) => setConfirm({
        title: 'Delete access profile',
        // Says what is lost and what is not. Deleting a profile that nothing uses is safe, and saying so
        // is better than a warning that makes the reader hesitate over a routine tidy-up.
        message: `Delete "${p.name}"? Its ${p.module_count} granted module`
            + `${Number(p.module_count) === 1 ? '' : 's'} and any redactions chosen on them go with it. `
            + (governs(p)
                // Says what stops working, in the operator's terms. A profile is additive, so what is
                // lost is whatever these callers reached ONLY through it — their own grants stay.
                ? `${governs(p)} follow this profile and lose whatever it granted them; `
                  + 'anything granted directly on a credential or binding is untouched.'
                : 'Nothing follows this profile, so nothing else changes.'),
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: async () => {
            try {
                await legacyUapiService.deleteAccessProfile(p.id);
                await load();
            } catch (e) {
                setError(apiErrorMessage(e, 'Failed to delete the profile'));
            }
        },
    });

    return (
        <>
            <div className="uapi-section-bar">
                <div>
                    <h2 className="uapi-section-title">Access profiles</h2>
                    <p className="lu-flow-note" style={{ margin: '0.25rem 0 0' }}>
                        A named set of routes and the redaction each call is served with, pointed at a
                        chain, at hotels, or at single credentials, and written once for all of them.
                    </p>
                </div>
                <Button variant="secondary" onClick={() => navigate('/universal-api/access-profiles/new')}>
                    <Plus className="icon" /> Add profile
                </Button>
            </div>

            {error && <p className="ppw-error-msg">{error}</p>}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading access profiles…</p></div>
            ) : profiles.length === 0 ? (
                <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>No access profiles yet.</p>
                    <p style={{ margin: '0.5rem auto 0', color: '#9ca3af', fontSize: '0.825rem', maxWidth: '58ch' }}>
                        Add one to describe what a kind of integration may call — the routes, what it is
                        allowed to see of each answer, and which properties follow it.
                    </p>
                </div>
            ) : (
                <div className="otx-table-wrap">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Modules</th>
                                <th>Calls named</th>
                                <th>Governs</th>
                                <th>Updated</th>
                                <th className="action-header" />
                            </tr>
                        </thead>
                        <tbody>
                            {profiles.map(p => (
                                /* THE ROW OPENS IT. Editing was reachable only from a button at the far
                                   right of a six-column table, so the obvious gesture — click the thing
                                   you want — did nothing at all. The button stays: it names the action
                                   for anyone who looks for one, and it is what a keyboard reaches. */
                                <tr
                                    key={p.id}
                                    className="clickable-row"
                                    title={`Open ${p.name}`}
                                    onClick={() => navigate(`/universal-api/access-profiles/${p.id}`)}
                                >
                                    <td><strong>{p.name}</strong></td>
                                    <td>{p.description || <span className="lu-muted">—</span>}</td>
                                    <td>{p.module_count}</td>
                                    {/* A module granted in full names no calls, and that zero means
                                        "everything under it" rather than "nothing" — said in words,
                                        because the number alone reads as the opposite. */}
                                    <td>
                                        {Number(p.operation_count) > 0
                                            ? p.operation_count
                                            : <span className="lu-muted">whole modules</span>}
                                    </td>
                                    <td>{governs(p) || <span className="lu-muted">nothing yet</span>}</td>
                                    <td>{formatDateTime(p.updated_at)}</td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button
                                            className="ppp-detail-btn ppp-detail-btn-primary"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/universal-api/access-profiles/${p.id}`); }}
                                        >
                                            <Pencil size={11} /> Edit
                                        </button>
                                        <button
                                            className="ppp-detail-btn"
                                            style={{ marginLeft: '0.35rem' }}
                                            onClick={(e) => { e.stopPropagation(); remove(p); }}
                                        >
                                            <Trash2 size={11} /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {confirm && (
                <ConfirmModal
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel={confirm.confirmLabel}
                    danger={confirm.danger}
                    onConfirm={() => { confirm.onConfirm(); setConfirm(null); }}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </>
    );
};

export default UapiAccessProfilesTab;
